"use client"

import { useEffect, useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { CompanyAutocomplete } from "@/components/community/company-autocomplete"
import { Pencil, Save, X, Phone, Briefcase, Tag, Building2 } from "lucide-react"
import { Contact } from "openapi/api/model"
import { getGetContactsQueryKey, useGetContacts, usePutContactsContactId } from "openapi/api/endpoints"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"

type ContactWithCompany = Contact & {
    companies?: {
        name?: string | null
    } | null
}

export function PersonContactDetails({ personId }: { personId: string }) {
    const [isEditing, setIsEditing] = useState(false)
    const [formData, setFormData] = useState<Contact>()
    const { data: contact } = useGetContacts({ contactId: personId })
    const person = contact?.[0] as ContactWithCompany | undefined
    const {mutateAsync:updateContact,isPending:isSaving}=usePutContactsContactId()
    const [companyName, setCompanyName] = useState<string>("")
    const queryClient = useQueryClient()

    useEffect(() => {
        setFormData(person)
        setCompanyName(person?.companies?.name ?? person?.company_name ?? "")
    }, [person])

    const handleSave = async () => {
        try{
            if(!formData){
                toast.warning("No contact data to save.")
                return
            }
            await updateContact({
                contactId:personId, 
                data:{
                    company_id: formData.company_id,
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    email: formData.email,
                    phone: formData.phone,
                    title: formData.title,
                    contact_type: formData.company_id? 'hiring_manager' : 'candidate'
                }
            })
            toast.success("Contact saved successfully.")
            await queryClient.invalidateQueries( {queryKey:getGetContactsQueryKey(), exact: false} )
            setIsEditing(false)
        }catch{
            toast.error("Failed to save contact. Please try again.")
            return
        }
        
    }

    const handleCancel = () => {
        setFormData(contact?.[0])
        setIsEditing(false)
    }

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle>Contact Details</CardTitle>
                        <CardDescription>Additional information about this contact</CardDescription>
                    </div>
                    {!isEditing && (
                        <Button onClick={() => setIsEditing(true)} size="sm" variant="outline">
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                        </Button>
                    )}
                </div>
            </CardHeader>
            {formData && (
            <CardContent>
                {isEditing ? (
                    <div className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="first_name">First Name</Label>
                                <Input
                                    id="first_name"
                                    value={formData?.first_name ?? ""}
                                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                    className="bg-white dark:bg-background text-black dark:text-foreground"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="last_name">Last Name</Label>
                                <Input
                                    id="last_name"
                                    value={formData?.last_name ?? ""}
                                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                    className="bg-white dark:bg-background text-black dark:text-foreground"
                                />
                            </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData?.email ?? ""}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="bg-white dark:bg-background text-black dark:text-foreground"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="phone">Phone</Label>
                                <Input
                                    id="phone"
                                    value={formData?.phone || ""}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    placeholder="Optional"
                                    className="bg-white dark:bg-background text-black dark:text-foreground"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="company">Company</Label>
                            <CompanyAutocomplete
                                value={companyName}
                                onChange={(value, id) => {
                                    setCompanyName(value)
                                    setFormData({ ...formData, company_id: id as string })
                                }}
                                placeholder="Search or create company..."
                                className="bg-white dark:bg-background text-black dark:text-foreground"
                            />
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="title">Title</Label>
                                <Input
                                    id="title"
                                    value={formData?.title ?? ''}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="e.g., Senior Software Engineer"
                                    className="bg-white dark:bg-background text-black dark:text-foreground"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="contact_type">Contact Type</Label>
                                <Select
                                    value={formData?.contact_type ?? ''}
                                    onValueChange={(value) =>
                                        setFormData({ ...formData, contact_type: value as Contact["contact_type"] })
                                    }
                                    disabled={true}
                                >
                                    <SelectTrigger
                                        id="contact_type"
                                        className="bg-white dark:bg-background text-black dark:text-foreground"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="candidate">Candidate</SelectItem>
                                        <SelectItem value="hiring_manager">Hiring Manager</SelectItem>
                                        <SelectItem value="other">Other</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="source">Source</Label>
                            <Input
                                id="source"
                                value={formData?.source ?? ""}
                                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                                placeholder="e.g., LinkedIn, Referral, Manual"
                                className="bg-white dark:bg-background text-black dark:text-foreground"
                            />
                        </div>

                        <div className="flex gap-2 pt-2">
                            <Button onClick={handleSave} disabled={isSaving}>
                                <Save className="h-4 w-4 mr-2" />
                                {isSaving ? "Saving..." : "Save Changes"}
                            </Button>
                            <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
                                <X className="h-4 w-4 mr-2" />
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-1">
                                <div className="text-sm text-muted-foreground">First Name</div>
                                <div className="font-medium">{formData?.first_name ?? "—"}</div>
                            </div>
                            <div className="space-y-1">
                                <div className="text-sm text-muted-foreground">Last Name</div>
                                <div className="font-medium">{formData?.last_name ?? "—"}</div>
                            </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-1">
                                <div className="text-sm text-muted-foreground">Email</div>
                                <div className="font-medium">{formData?.email ?? ''}</div>
                            </div>
                            <div className="space-y-1">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Phone className="h-3.5 w-3.5" />
                                    Phone
                                </div>
                                <div className="font-medium">{formData?.phone ?? "—"}</div>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Building2 className="h-3.5 w-3.5" />
                                Company
                            </div>
                            <div className="font-medium">
                                {(formData as ContactWithCompany).companies?.name ?? (formData as ContactWithCompany).company_name ?? "—"}
                            </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-1">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Briefcase className="h-3.5 w-3.5" />
                                    Title
                                </div>
                                <div className="font-medium">{formData?.title || "—"}</div>
                            </div>
                            <div className="space-y-1">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Tag className="h-3.5 w-3.5" />
                                    Contact Type
                                </div>
                                <div className="font-medium capitalize">{formData?.contact_type?.replace("_", " ")}</div>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <div className="text-sm text-muted-foreground">Source</div>
                            <div className="font-medium">{formData?.source || "—"}</div>
                        </div>
                    </div>
                )}
            </CardContent>                
            )}

        </Card>
    )
}
